import React, { PropsWithChildren, useEffect } from 'react';
import styles from './PlayToMint.module.scss';
import twilightShift from '../../assets/img/play-to-mint/twilight_shift_poster.jpg';
import leftEgg from '../../assets/img/play-to-mint/stage3-level1.png';
import rightEgg from '../../assets/img/play-to-mint/stage1-lvl1 1.png';
import ellipse from '../../assets/img/play-to-mint/ellipse.png';
import { Instruction } from '../Instruction/Instruction';
import { HorizontalDivider } from '../HorizontalDivider/HorizontalDivider';
import { Button } from '../Button/Button';
import { Link, useParams } from 'react-router-dom';
import { useUser } from '../../hooks/useUser';
import { Route, routes } from '../../routing/routes';
import { PlatformButton } from '../Button/PlatformButton';
import { setReferralId } from 'contexts/UserReferralContext';

export function PlayToMintPlatformDisclaimer({ omnigrow }: PlayToMintPlatformDisclaimerProps) {
  if (omnigrow) {
    return (
      <p className={styles.disclaimer}>
        <i>
          The DuskBreakers Game Launcher with Twilight Shift is currently only available for Windows
          PC, but Mac support is coming soon! If you don’t have a Windows PC, you can collect
          OmniGrow by playing the Beast Battler game in your web browser{' '}
          <Link to={routes.game()}>here</Link>.
        </i>
      </p>
    );
  }

  return (
    <p className={styles.disclaimer}>
      <i>
        The DuskBreakers Game Launcher with Twilight Shift is currently only available for Windows
        PC, but Mac support is coming soon! If you don’t have a Windows PC, you can qualify for
        play-to-mint by playing the Beast Battler game in your web browser{' '}
        <Link to={routes.game()}>here</Link>.
      </i>
    </p>
  );
}

export type PlayToMintPlatformDisclaimerProps = {
  omnigrow?: boolean;
};

function CTAButton({ children }: PropsWithChildren<{}>) {
  return (
    <Button primary className={styles.playToMintButton}>
      {children}
    </Button>
  );
}

function CTA(props: PlayToMintProps) {
  const { account, ethereum } = useUser();
  const isLoggedIn = Boolean(account.dusktech?.userId);
  const hasWalletConnected = ethereum.isConnected;

  if (!isLoggedIn) {
    return (
      <div>
        <Link to={props.callToAction.href}>
          <CTAButton>{props.callToAction.text}</CTAButton>
        </Link>
        <p className="mt-3">You need to be logged-in to proceed.</p>
      </div>
    );
  }

  if (!hasWalletConnected) {
    return (
      <div>
        <Link to={routes.login()}>
          <CTAButton>Connect Wallet</CTAButton>
        </Link>
        <p className="mt-3">Please connect your Ethereum wallet to proceed.</p>
      </div>
    );
  }

  return (
    <div className="stacked-aligned-buttons">
      <PlatformButton platform="windows" text="Play to Mint" link={routes.launcher()} />
      <PlatformButton platform="macos" text="Play to Mint" link={routes.game()} />
    </div>
  );
}

export function PlayToMint(props: PlayToMintProps) {
  const { referralCode } = useParams();

  useEffect(() => {
    if (referralCode) {
      setReferralId(referralCode);
    }
  }, [referralCode]);

  return (
    <article className={styles.playToMintPage}>
      <img src={ellipse} className={styles.ellipse} alt="Faded-out ellipse" />
      <div className={styles.info}>
        <h1>Play To Mint</h1>
        <p>
          Play our newest game ‘Twilight Shift’ to win an Allow List spot, which enables you to mint
          up to three (3) Gribble Eggs if you play before August 21st 6PM PDT after which playing
          will grant two (2) mints.
          <br />
          <br />
          Explore the forbidden Red Warrens, collect valuable Duskite materials, and navigate around
          agitated inhabitants.
        </p>
        <CTA callToAction={props.callToAction} />
        <PlayToMintPlatformDisclaimer />
        <HorizontalDivider className={styles.divider} />
        <h2 className={styles.subtitle}>How it works?</h2>
        <StepOne />
        <StepTwo />
        <StepThree />
      </div>
      <aside className={styles.graphics}>
        <div className={styles.twilightShiftLogoContainer}>
          <img src={leftEgg} className={styles.eggLeft} alt="Gribble egg" />
          <img
            src={twilightShift}
            className={styles.twilightShiftLogo}
            alt="Twilight shift poster"
          />
          <img src={rightEgg} className={styles.eggRight} alt="Gribble egg" />
        </div>
      </aside>
    </article>
  );
}

type PlayToMintCTA = {
  text: string;
  href: Route;
};

export type PlayToMintProps = {
  callToAction: PlayToMintCTA;
};

function StepOne() {
  return (
    <Instruction
      number={1}
      title="Connect your Ethereum wallet"
      description={
        <>
          Go to <Link to={routes.login()}>Play Now {'>'} Account</Link> and connect your Ethereum
          wallet.
        </>
      }
    />
  );
}

function StepTwo() {
  return (
    <Instruction
      number={2}
      title="Play Twilight Shift"
      description={
        <>
          <Link to={routes.launcher()}>Download</Link>, install, and play our newest game and score
          some points.
        </>
      }
    />
  );
}

function StepThree() {
  return (
    <Instruction
      number={3}
      title="Mint Your NFTs"
      description={
        <>
          <Link to={routes.mint()}>Mint</Link> your Gribble Egg NFTs and start the Eggsperience to
          level up your Eggs. Please give <span className={styles.highlight}>up to one hour</span>{' '}
          to reflect your mint ability on the website.
        </>
      }
    />
  );
}
